import React, { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Search, Layers, Plus, Eye, ArrowLeftRight, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const fmt = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const ShareAccountsLookupPage = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [clientResult, setClientResult] = useState(null);
  const [shareAccounts, setShareAccounts] = useState([]);
  const [multipleResults, setMultipleResults] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showTransfer, setShowTransfer] = useState(null);
  const [showRedeem, setShowRedeem] = useState(null);
  const [clients, setClients] = useState([]);
  const [createForm, setCreateForm] = useState({ product_id: '', shares_requested: '', investor_type: 'client' });
  const [transferForm, setTransferForm] = useState({ to_client_id: '', shares: '' });
  const [redeemForm, setRedeemForm] = useState({ shares: '' });
  const [showSubscribeNew, setShowSubscribeNew] = useState(false);
  const [subscribeClientSearch, setSubscribeClientSearch] = useState('');
  const [subscribeClients, setSubscribeClients] = useState([]);
  const [subscribeSelectedClient, setSubscribeSelectedClient] = useState(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setClientResult(null);
    setShareAccounts([]);
    setMultipleResults([]);
    try {
      const res = await api.get(`/clients/lookup?q=${encodeURIComponent(query.trim())}`);
      if (res.data.match === 'single') {
        setClientResult(res.data.client);
        const saRes = await api.get(`/share-accounts?client_id=${res.data.client.id}`);
        setShareAccounts(saRes.data || []);
      } else if (res.data.match === 'multiple') {
        setMultipleResults(res.data.clients);
      } else {
        toast.error('No client found.');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Search failed'); }
    setSearching(false);
  };

  const selectClient = async (clientId) => {
    setSearching(true);
    try {
      const [cRes, saRes] = await Promise.all([
        api.get(`/clients/lookup?q=${clientId}`),
        api.get(`/share-accounts?client_id=${clientId}`),
      ]);
      if (cRes.data.match === 'single') {
        setClientResult(cRes.data.client);
        setShareAccounts(saRes.data || []);
        setMultipleResults([]);
      }
    } catch { toast.error('Failed to load client'); }
    setSearching(false);
  };

  const openSubscribe = async () => {
    setShowCreate(true);
    try { const res = await api.get('/share-products'); setProducts((res.data || []).filter(p => p.is_active !== false)); } catch {}
  };

  const openSubscribeNew = async () => {
    setShowSubscribeNew(true);
    setSubscribeSelectedClient(null);
    setSubscribeClientSearch('');
    setSubscribeClients([]);
    try { const res = await api.get('/share-products'); setProducts((res.data || []).filter(p => p.is_active !== false)); } catch {}
  };

  const searchSubscribeClients = async (q) => {
    setSubscribeClientSearch(q);
    if (q.length < 2) { setSubscribeClients([]); return; }
    try {
      const res = await api.get(`/clients/lookup?q=${encodeURIComponent(q)}`);
      if (res.data.match === 'multiple') setSubscribeClients(res.data.clients || []);
      else if (res.data.match === 'single' && res.data.client) setSubscribeClients([res.data.client]);
      else setSubscribeClients([]);
    } catch { setSubscribeClients([]); }
  };

  const handleSubscribeNew = async () => {
    if (!subscribeSelectedClient) { toast.error('Select a client first'); return; }
    try {
      await api.post('/share-accounts', { client_id: subscribeSelectedClient.id, product_id: createForm.product_id, shares_requested: parseInt(createForm.shares_requested), investor_type: createForm.investor_type });
      toast.success('Share subscription created — pending approval');
      setShowSubscribeNew(false);
      setCreateForm({ product_id: '', shares_requested: '', investor_type: 'client' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCreate = async () => {
    try {
      await api.post('/share-accounts', { ...createForm, client_id: clientResult.id, shares_requested: parseInt(createForm.shares_requested) });
      toast.success('Share subscription created — pending approval');
      setShowCreate(false);
      selectClient(clientResult.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/share-accounts/${id}/approve`);
      toast.success('Shares approved and issued');
      selectClient(clientResult.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRedeem = async () => {
    try {
      const res = await api.post(`/share-accounts/${showRedeem.id}/redeem`, { shares: parseInt(redeemForm.shares) });
      toast.success(res.data.message);
      setShowRedeem(null);
      selectClient(clientResult.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDividendWithdraw = async (a) => {
    try {
      const res = await api.post(`/share-accounts/${a.id}/withdraw-dividends`, { amount: a.dividends_earned });
      toast.success(res.data.message);
      selectClient(clientResult.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleTransfer = async () => {
    try {
      if (!clients.length) { const r = await api.get('/clients?limit=200'); setClients(r.data.clients || r.data || []); }
      const res = await api.post(`/share-accounts/${showTransfer.id}/transfer`, { to_client_id: transferForm.to_client_id, shares: parseInt(transferForm.shares) });
      toast.success(res.data.message);
      setShowTransfer(null);
      selectClient(clientResult.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openTransfer = async (a) => {
    setShowTransfer(a);
    setTransferForm({ to_client_id: '', shares: '' });
    if (!clients.length) { try { const r = await api.get('/clients?limit=200'); setClients(r.data.clients || r.data || []); } catch {} }
  };

  const c = clientResult;
  const canApprove = ['admin', 'manager'].includes(user?.role);

  return (
    <div className="space-y-6 fade-in" data-testid="share-lookup-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Clients</span><span>/</span><span className="text-[#1f1f1f] font-medium">Share Account Lookup</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">SHARE MANAGEMENT</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Share Account Lookup</h1>
          <p className="text-sm text-gray-500 mt-1">Search by client name, account number, or ID number</p>
        </div>
        <Button onClick={openSubscribeNew} className="bg-[#1f1f1f] hover:bg-[#333333] text-white text-sm gap-1.5" data-testid="lookup-subscribe-btn">
          <Plus className="w-4 h-4" /> Subscribe to Shares
        </Button>
      </div>

      <form onSubmit={handleSearch} className="flex gap-3" data-testid="share-search-form">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Enter client name, ID number, or account number..." className="pl-12 py-6 text-base" data-testid="share-lookup-input" />
        </div>
        <Button type="submit" disabled={searching} className="bg-[#1f1f1f] hover:bg-[#333333] px-8 py-6" data-testid="share-lookup-btn">{searching ? 'Searching...' : 'Search'}</Button>
      </form>

      {multipleResults.length > 0 && (
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-sm font-medium text-[#1f1f1f] mb-3">Multiple clients found — select one:</p>
          <div className="space-y-2">
            {multipleResults.map(mc => (
              <button key={mc.id} onClick={() => selectClient(mc.client_number || mc.id_number)} className="w-full flex items-center gap-4 p-3 border border-gray-200 hover:bg-gray-50 text-left">
                <div><p className="text-sm font-medium text-[#1f1f1f]">{mc.first_name} {mc.last_name}</p><p className="text-xs text-gray-500">{mc.client_number} | ID: {mc.id_number}</p></div>
              </button>
            ))}
          </div>
        </div>
      )}

      {c && (
        <div className="space-y-4">
          <div className="bg-[#1f1f1f] text-white p-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold">{c.first_name} {c.last_name}</h2>
              <p className="text-xs text-gray-400">{c.client_number} | {c.id_number}</p>
            </div>
            <Button size="sm" onClick={openSubscribe} className="bg-white/10 hover:bg-white/20 text-white text-xs" data-testid="subscribe-share-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Subscribe to Shares
            </Button>
          </div>

          {shareAccounts.length === 0 ? (
            <div className="text-center py-8 bg-white border border-gray-200">
              <Layers className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No share accounts for this client</p>
            </div>
          ) : (
            <div className="space-y-3">
              {shareAccounts.map(a => (
                <div key={a.id} className="bg-white border border-gray-200 p-4" data-testid={`share-card-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#1f1f1f]">{a.account_number}</p>
                      <p className="text-xs text-gray-500">{a.product_name} | <span className="capitalize">{(a.status || '').replace(/_/g, ' ')}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#1f1f1f]">{a.shares_approved} shares</p>
                      <p className="text-xs text-gray-400">Value: {fmt(a.total_value)} | Dividends: {fmt(a.dividends_earned)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                    {a.status === 'pending_approval' && canApprove && (
                      <Button size="sm" className="bg-green-600 text-white text-xs" onClick={() => handleApprove(a.id)}>Approve</Button>
                    )}
                    {a.status === 'active' && (
                      <>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => openTransfer(a)}><ArrowLeftRight className="w-3 h-3 mr-1" />Transfer</Button>
                        <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200" onClick={() => { setShowRedeem(a); setRedeemForm({ shares: '' }); }}>Sell</Button>
                        {(a.dividends_earned || 0) > 0 && (
                          <Button size="sm" variant="outline" className="text-xs text-emerald-600 border-emerald-200" onClick={() => handleDividendWithdraw(a)} data-testid={`dividend-withdraw-${a.id}`}>
                            <DollarSign className="w-3 h-3 mr-1" />Withdraw Dividends ({fmt(a.dividends_earned)})
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-xs" onClick={() => setShowDetail(a)}><Eye className="w-3 h-3 mr-1" />History</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!clientResult && multipleResults.length === 0 && !searching && (
        <div className="text-center py-16"><Search className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">Search for a client to view their share accounts</p></div>
      )}

      {/* Subscribe Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" aria-describedby="sl-create-desc">
          <DialogHeader><DialogTitle>Subscribe to Shares</DialogTitle></DialogHeader>
          <p id="sl-create-desc" className="sr-only">Subscribe client to shares</p>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Client: <span className="font-medium text-[#1f1f1f]">{c?.first_name} {c?.last_name}</span></p>
            <div><Label>Share Product *</Label>
              <Select value={createForm.product_id} onValueChange={v => setCreateForm({...createForm, product_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — R{p.unit_price || p.nominal_price || 0}/share</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Number of Shares *</Label><Input type="number" min="1" value={createForm.shares_requested} onChange={e => setCreateForm({...createForm, shares_requested: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#1f1f1f] text-white">Subscribe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto" aria-describedby="sl-detail-desc">
          <DialogHeader><DialogTitle>{showDetail?.account_number} — History</DialogTitle></DialogHeader>
          <p id="sl-detail-desc" className="sr-only">Transaction history</p>
          {showDetail?.transactions?.length > 0 ? (
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50 border-b"><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th><th className="text-right px-3 py-2">Shares</th><th className="text-right px-3 py-2">Amount</th></tr></thead>
              <tbody>{showDetail.transactions.map((t, i) => (<tr key={i} className="border-b"><td className="px-3 py-1.5">{t.date?.slice(0, 10)}</td><td className="px-3 py-1.5 capitalize">{(t.type || '').replace(/_/g, ' ')}</td><td className="px-3 py-1.5 text-right font-mono">{t.shares}</td><td className="px-3 py-1.5 text-right font-mono">{fmt(t.total_amount)}</td></tr>))}</tbody>
            </table>
          ) : <p className="text-sm text-gray-400 text-center py-4">No transactions yet</p>}
        </DialogContent>
      </Dialog>

      {/* Redeem Dialog */}
      <Dialog open={!!showRedeem} onOpenChange={() => setShowRedeem(null)}>
        <DialogContent className="max-w-sm" aria-describedby="sl-redeem-desc">
          <DialogHeader><DialogTitle>Sell / Redeem Shares</DialogTitle></DialogHeader>
          <p id="sl-redeem-desc" className="sr-only">Sell shares</p>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">{showRedeem?.shares_approved} shares @ {fmt(showRedeem?.unit_price)}/share</p>
            <div><Label>Shares to Sell *</Label><Input type="number" min="1" max={showRedeem?.shares_approved} value={redeemForm.shares} onChange={e => setRedeemForm({...redeemForm, shares: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeem(null)}>Cancel</Button>
            <Button onClick={handleRedeem} className="bg-red-600 text-white">Sell</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={!!showTransfer} onOpenChange={() => setShowTransfer(null)}>
        <DialogContent className="max-w-md" aria-describedby="sl-transfer-desc">
          <DialogHeader><DialogTitle>Transfer Shares</DialogTitle></DialogHeader>
          <p id="sl-transfer-desc" className="sr-only">Transfer shares</p>
          <div className="space-y-4">
            <div><Label>To Client *</Label>
              <Select value={transferForm.to_client_id} onValueChange={v => setTransferForm({...transferForm, to_client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.filter(cc => cc.id !== c?.id).slice(0, 50).map(cc => <SelectItem key={cc.id} value={cc.id}>{cc.first_name} {cc.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Shares to Transfer *</Label><Input type="number" min="1" max={showTransfer?.shares_approved} value={transferForm.shares} onChange={e => setTransferForm({...transferForm, shares: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(null)}>Cancel</Button>
            <Button onClick={handleTransfer} className="bg-[#1f1f1f] text-white">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscribe New (no client context) */}
      <Dialog open={showSubscribeNew} onOpenChange={setShowSubscribeNew}>
        <DialogContent className="max-w-lg" aria-describedby="sl-subscribe-new-desc">
          <DialogHeader><DialogTitle>Subscribe to Shares</DialogTitle></DialogHeader>
          <p id="sl-subscribe-new-desc" className="sr-only">Subscribe a client to shares</p>
          <div className="space-y-4">
            <div>
              <Label>Search Client *</Label>
              <Input placeholder="Type client name, ID, or account number..." value={subscribeClientSearch} onChange={e => searchSubscribeClients(e.target.value)} className="text-sm" data-testid="subscribe-client-search" />
              {subscribeClients.length > 0 && (
                <div className="max-h-32 overflow-y-auto border border-gray-200 bg-white mt-1">
                  {subscribeClients.map(sc => (
                    <button key={sc.id} onClick={() => { setSubscribeSelectedClient(sc); setSubscribeClientSearch(`${sc.first_name} ${sc.last_name}`); setSubscribeClients([]); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${subscribeSelectedClient?.id === sc.id ? 'bg-blue-50' : ''}`}>
                      {sc.first_name} {sc.last_name} <span className="text-gray-400">({sc.client_number || sc.id_number})</span>
                    </button>
                  ))}
                </div>
              )}
              {subscribeSelectedClient && <p className="text-xs text-green-600 mt-1">Selected: {subscribeSelectedClient.first_name} {subscribeSelectedClient.last_name}</p>}
            </div>
            <div><Label>Share Product *</Label>
              <Select value={createForm.product_id} onValueChange={v => setCreateForm({...createForm, product_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — R{p.unit_price || p.nominal_price || 0}/share</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Number of Shares *</Label><Input type="number" min="1" value={createForm.shares_requested} onChange={e => setCreateForm({...createForm, shares_requested: e.target.value})} data-testid="subscribe-qty" /></div>
              <div><Label>Investor Type</Label>
                <Select value={createForm.investor_type} onValueChange={v => setCreateForm({...createForm, investor_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="dfi">DFI</SelectItem>
                    <SelectItem value="institution">Institution</SelectItem>
                    <SelectItem value="group">Group</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscribeNew(false)}>Cancel</Button>
            <Button onClick={handleSubscribeNew} className="bg-[#1f1f1f] text-white" data-testid="subscribe-new-submit">Subscribe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShareAccountsLookupPage;
